import * as Yup from "yup";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import {makeRequired, makeValidate} from "mui-rff";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../hooks/use-is-mounted";
import {CircularProgress, DialogActions, DialogContent, DialogTitle, Fade, SvgIcon, Tooltip} from "@mui/material";
import {Col} from "reactstrap";
import Button from "../../base/button/button";
import Dialog from "../../base/dialog";
import Form from "../../base/form";
import {OutlinedInputForm} from "../../base/input/outlined_input";
import TryAgain from "../../app-specific/try-again";
import RemoveConfirmationDialog from "../remove-confirmation";
import classnames from "classnames";
import SaveOrRollback from "../../app-specific/save-or-rollback";
import IconButton from "../../base/button/icon_button";
import {Edit} from "@mui/icons-material";
import {ReactComponent as RemoveIcon} from "../../../../assets/images/remove.svg";
import {ReactComponent as DefaultIcon} from "../../../../assets/images/default-icon.svg";

import Api from "../../../../core/services/api/api";

const schema = Yup.object().shape({
    title: Yup.string().required(ValidateMessages.required)
});

const validate = makeValidate(schema);
const required = makeRequired(schema);

const removeConfirmationDialogInitialState = {
    open: false,
    data: [],
    entity: null,
}

const ManagePartSupportVendorStatusesDialog = ({open, onClose}) => {
    const [removeConfirmationState, setRemoveConfirmationState] = useState(removeConfirmationDialogInitialState);
    const [statuses, setStatuses] = useState([])
    const [loading, setLoading] = useState(false)
    const [creating, setCreating] = useState(false)
    const isMounted = useIsMounted()

    /**
     * Fetches the notification categories of the system form the server and populates the state list.
     */
    const getStatuses = useCallback(async () => {
        setLoading(true);
        const response = await Api.getPartSupportVendorStatuses();
        if (!isMounted())
            return;
        setLoading(false);
        if (response?.resultFlag) {
            setStatuses(response?.data?.map(e => ({
                ...e,
                editing: false,
                inEditMode: false,
            })) ?? []);
        }
    }, [isMounted])

    /**
     * Gets the categories from the server when the dialog is opened.
     */
    useEffect(() => {
        if (open) {
            getStatuses().then();
        }
    }, [getStatuses, open])

    /**
     * Close the dialog and sends the statuses to the parent component.
     * @type {(function(): void)|*}
     */
    const closeDialog = useCallback(() => {
        const data = loading ? undefined : statuses;
        onClose(data);
    }, [loading, onClose, statuses])

    /**
     * Create a new status with the given values and adds it to the list.
     * @param {any} values the form values
     * @param {formApi} form the form
     * @return {Promise<boolean>}
     */
    const createStatus = useCallback(async (values, form) => {
        setCreating(true)
        const forApi = {
            title: values.title,
        }
        const response = await Api.createPartSupportVendorStatus(forApi)
        if (!isMounted()) return false
        if (response?.resultFlag) {
            setStatuses(prevState => [
                ...prevState,
                {...response.data, editing: false, inEditMode: false}
            ])
            form.change('title', '')
            form.resetFieldState('title');
        }
        setCreating(false)
        return false
    }, [isMounted])

    /**
     * Updates a status with the given values.
     * @param {any} status the status to be updated.
     * @return {Promise<boolean>}
     */
    const updateStatus = useCallback(async (status) => {
        setStatuses(prevState => prevState.map(e => e.id === status.id ? {
            ...e,
            editing: true
        } : e))
        const forApi = {
            title: status.title,
            id: status.id,
            isDefault: status.isDefault,
            updatedProperties: ['Title'],
        }
        const response = await Api.updatePartSupportVendorStatus(forApi)
        if (!isMounted())
            return false
        setStatuses(prevState => prevState.map(e => e.id === status.id
            ? {...e, editing: false, ...(response?.resultFlag ? {inEditMode: false, title: forApi.title} : {})}
            : e
        ))
        return response?.resultFlag ?? false
    }, [isMounted])

    /**
     * Sets the given status as default.
     * @type {(function(*): Promise<boolean|*>)}
     */
    const setStatusAsDefault = useCallback(async (status) => {
        setStatuses(prevState => prevState.map(e => ({...e, editing: true})));
        const forApi = {
            title: status.title,
            id: status.id,
            isDefault: true,
            updatedProperties: ['IsDefault'],
        }
        const response = await Api.updatePartSupportVendorStatus(forApi)
        if (!isMounted())
            return false
        if (response?.resultFlag) {
            setStatuses(prevState => prevState.map(e => ({...e, editing: false, isDefault: e.id === status.id})));
        }
        return response?.resultFlag ?? false
    }, [isMounted])

    /**
     * Removes a status from the server and then removes it from the local list.
     * @param {any} status the status to be removed
     */
    const removeStatus = useCallback(async (status, replacement) => {
        setStatuses(prevState => prevState.map(e => e.id === status.id ? {
            ...e,
            editing: true
        } : e))
        const response = await Api.removePartSupportVendorStatus(status.id, replacement?.id)
        if (!isMounted())
            return false;
        if (response?.resultFlag) {
            setStatuses(prevState => prevState.filter(e => e.id !== status.id))
        } else {
            setStatuses(prevState => prevState.map(e => e.id === status.id ? {
                ...e,
                editing: false
            } : e))
        }
        return response?.resultFlag ?? false;
    }, [isMounted])

    /**
     * Toggles the edit mode of the status cards.
     * @param {any} status
     * @param {boolean} inEditMode
     */
    const toggleEditMode = useCallback((status, inEditMode = false) => {
        setStatuses(prevState => prevState.map(e => e.id === status.id ? {...e, inEditMode} : e))
    }, [])

    return (
        <>
            <RemoveConfirmationDialog
                open={removeConfirmationState.open}
                close={() => setRemoveConfirmationState(removeConfirmationDialogInitialState)}
                data={removeConfirmationState.data}
                remove={(replacement) => removeStatus(removeConfirmationState.entity, replacement)}
                removedEntryTitle={removeConfirmationState.entity?.title}
            />
            <Dialog
                open={open}
                setOpen={closeDialog}
                maxWidth={'sm'}
                fullWidth
            >
                <DialogTitle>
                    Manage Part Support Vendor Statuses
                </DialogTitle>
                <DialogContent className={'d-flex flex-wrap w-100'}>
                    <Col xs={12}>
                        <p className={'text-md font-weight-semi-bold mt-2 mb-4'}>
                            Create a new status
                        </p>
                    </Col>
                    <Form
                        className='d-flex flex-wrap w-100 align-items-center'
                        onSubmit={createStatus}
                        validate={validate}
                        initialValues={{}}
                        render={({values, form}) => {
                            return (
                                <>
                                    <Col className={'pl-3'}>
                                        <OutlinedInputForm
                                            name={'title'}
                                            placeholder={'title'}
                                            label={"Title"}
                                            required={required.title}
                                            value={values.title ?? ''}
                                            onChange={(e) => form.change(e.target.name, e.target.value)}
                                        />
                                    </Col>
                                    <Col xs={'auto'}>
                                        <Button
                                            className='py-2-5 px-5'
                                            type={'submit'}
                                            variant={'contained'}
                                            color={'primary'}
                                            disabled={creating}
                                        >
                                            {
                                                creating
                                                    ? "Adding..."
                                                    : "Add"
                                            }
                                        </Button>
                                    </Col>
                                </>
                            )
                        }}
                    />
                    <Col xs={12} className={'d-flex flex-wrap mt-5'}>
                        {
                            loading
                                ? Array(4).fill(null).map((e, i) => (
                                    <div key={i} className={'loading normal w-100 mb-3'}>
                                        <div/>
                                    </div>
                                ))
                                : statuses?.length < 1
                                    ? <TryAgain
                                        text={"There is currently no statuses submitted. you can add one  using the form above."}
                                    />
                                    : statuses?.map(status => (
                                        <StatusCard
                                            key={status.id}
                                            status={status}
                                            update={updateStatus}
                                            setDefault={setStatusAsDefault}
                                            remove={() => setRemoveConfirmationState({
                                                open: true,
                                                data: statuses.filter(e => e.id !== status.id)
                                                    .map(e => ({
                                                        title: e.title,
                                                        id: e.id,
                                                        data: e,
                                                    })),
                                                entity: status,
                                            })}
                                            setEditing={(e) => toggleEditMode(status, e)}
                                        />
                                    ))
                        }
                    </Col>
                </DialogContent>
                <DialogActions/>
            </Dialog>
        </>
    );
}

const StatusCard = ({status, update, setDefault, remove, setEditing}) => {
    const [hovered, setHovered] = useState(false);
    const initialValues = useMemo(() => ({
        title: status?.title ?? '',
    }), [status?.title])

    const editing = useMemo(() => status?.inEditMode, [status?.inEditMode]);

    /**
     * Prepares the new version of the category to be sent through api and then based on the returned resultFlag,
     * remains in or gets out of the edit mode.
     * @param {mode} values formValues
     */
    const updateStatus = useCallback((values) => {
        if (values?.title === status?.title) {
            setEditing(false)
            return
        }
        const newStatus = {
            ...status,
            title: values.title,
        }
        update(newStatus).then();
    }, [setEditing, status, update])

    /**
     * Reverts the values of this template.
     * @param {any} values
     * @param {formApi} form
     */
    const revertValues = (values, form) => {
        setEditing(false)
        form.reset(initialValues)
    }


    return (
        <>
            <div
                className={classnames('part-support-vendor-status-card', {'editing': editing})}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
            >
                <Form
                    className={''}
                    onSubmit={() => false}
                    validate={validate}
                    initialValues={initialValues}
                    render={({values, form}) => {
                        return (
                            <>
                                <div className={'d-flex flex-column'}>
                                    <div className={classnames('fade-container', {
                                        'expanded': editing,
                                    })}>
                                        <Fade in={editing}
                                              className={'d-flex justify-content-between align-items-center'}>
                                            <div>
                                                <OutlinedInputForm
                                                    name={'title'}
                                                    placeholder={'title'}
                                                    label={"Title"}
                                                    multiline
                                                    className={'mr-2 ml-5 max-w-80'}
                                                    required={required.title}
                                                    value={values.title ?? ''}
                                                    onChange={(e) => form.change(e.target.name, e.target.value)}
                                                />
                                                <SaveOrRollback
                                                    column={false}
                                                    loading={status.editing}
                                                    show={true}
                                                    onRollback={() => revertValues(values, form)}
                                                    onSave={() => updateStatus(values)}
                                                />
                                            </div>
                                        </Fade>
                                        <Fade in={!editing}
                                              className={'d-flex align-items-center justify-content-between'}>
                                            <div>
                                                <Tooltip
                                                    title={status.isDefault
                                                        ? "This is the default status"
                                                        : "Make This the new default status"
                                                    }
                                                >
                                                   <span>
                                                        <IconButton
                                                            onPressed={() => setDefault(status)}
                                                            disabled={status?.editing || status.isDefault}
                                                            color={'paper.secondary'}
                                                            className={'ml-2 mr-2'}
                                                        >
                                                    {
                                                        status.editing &&
                                                        <CircularProgress size={20}/>
                                                    }
                                                            {
                                                                !status.editing && status.isDefault &&
                                                                <DefaultIcon/>
                                                            }
                                                            {
                                                                !status.editing && !status.isDefault &&
                                                                <SvgIcon
                                                                    className={'transition'}
                                                                    component={DefaultIcon}
                                                                    sx={{
                                                                        opacity: hovered ? 1 : 0,
                                                                        'path': {fill: 'secondary.main'}
                                                                    }}
                                                                />
                                                            }
                                                </IconButton>
                                                   </span>
                                                </Tooltip>
                                                <p className={'title flex-grow-1'}>
                                                    {status?.title ?? ''}
                                                </p>
                                                <div className={'d-flex justify-content-end align-items-center'}>
                                                    <IconButton
                                                        disabled={status.editing || editing}
                                                        className={"mx-1 ml-2"}
                                                        color={'paper.secondary'}
                                                        onPressed={() => setEditing(true)}
                                                    >
                                                        <Edit className={"icon normal"}/>
                                                    </IconButton>
                                                    {
                                                        !status.isDefault &&
                                                        <IconButton
                                                            onPressed={() => remove(status)}
                                                            disabled={status?.editing}
                                                            color={'paper.error'}
                                                            className={'ml-2'}
                                                        >
                                                            <SvgIcon component={RemoveIcon} className='icon normal'/>
                                                        </IconButton>
                                                    }
                                                </div>
                                            </div>
                                        </Fade>
                                    </div>
                                </div>
                            </>
                        );
                    }}
                />
            </div>
        </>
    );
}

export default ManagePartSupportVendorStatusesDialog;
